import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Input,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import usePersistStoreData from "../Hooks/usePersistStoreData";
import { Formik, Field, ErrorMessage, Form } from "formik";
import * as Yup from "yup";
import {
  reKyc_UpdatePersonalDetailsThunk,
  reKyc_email_verify_otpThunk,
  reKyc_phone_verify_otpThunk,
  reKyc_send_email_otpThunk,
  reKyc_send_phone_otpThunk,
} from "../../slices/thunks";
import { useSelector } from "react-redux";
import { showToast } from "../ToastNotification";

const EditEmailMobileModal = ({
  edit_MailMobile,
  close_editMailMobile,
  mode,
  heading,
  clearError,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData } = usePersistStoreData();
  const token = userData?.token;

  const [showOTP, setShowOTP] = useState(false);
  const [timer, setTimer] = useState(60);
  const [resendActive, setResendActive] = useState(false);

  const { reKyc_send_email_otpData, reKyc_send_email_otpLoading } = useSelector(
    (state) => state.Re_Kyc_Send_Email_Otp
  );

  const { reKyc_send_phone_otpLoading } = useSelector(
    (state) => state.Re_Kyc_Send_Phone_Otp
  );

  const { reKyc_UpdatePersonalDetailData, reKyc_UpdatePersonalDetailLoading } =
    useSelector((state) => state.Re_Kyc_Update_Personal_Details);

  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return email;
    const [user, domain] = email.split("@");
    const visible = user.slice(0, 2);
    return `${visible}****@${domain}`;
  };

  const maskMobile = (mobile) => {
    if (!mobile || mobile.length < 4) return mobile;
    const last4 = mobile.slice(-4);
    return `******${last4}`;
  };

  const goToOTP = (values) => {
    setShowOTP(true);
    setTimer(60); // restart timer
    setResendActive(false); // disable resend until timer completes
  };

  const initialValues = {
    mobile: "",
    email: "",
    otp: ["", "", "", ""],
  };

  const validationSchema = (mode, otpStep) =>
    Yup.object().shape({
      ...(mode === "phone"
        ? {
            mobile: Yup.string()
              .required("Mobile number is required")
              .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number")
              .test(
                "not-same-mobile",
                "Please enter a different mobile number",
                function (value) {
                  const existingMobile = userData?.personal_details?.mobile;
                  return value !== existingMobile;
                }
              ),
          }
        : {
            email: Yup.string()
              .required("Email is required")
              .email("Enter a valid email")
              .test(
                "not-same-email",
                "Please enter a different email address",
                function (value) {
                  const existingEmail = userData?.personal_details?.email;
                  return value !== existingEmail;
                }
              ),
          }),
      ...(otpStep && {
        otp: Yup.array()
          .of(
            Yup.string()
              .matches(/^\d$/, "Only digits allowed")
              .required("Required")
          )
          .length(4, "OTP must be 4 digits"),
      }),
    });
  const handleResend = (values) => {
    if (resendActive) {
      handleSubmit(values);
      setTimer(60);
      setResendActive(false);
    }
  };

  const handleSubmit = (values) => {
    const emailParams = { params: { email: values?.email }, token };
    const mobileParams = { params: { phone: values?.mobile }, token };

    const thunk =
      mode === "email"
        ? reKyc_send_email_otpThunk(emailParams)
        : reKyc_send_phone_otpThunk(mobileParams);

    dispatch(thunk)
      .unwrap()
      .then((res) => {
        if (res?.code === 200) {
          showToast(res?.message, "success");
          goToOTP(values);
        } else {
          showToast(
            res?.message || "Something went wrong, please try again",
            "error"
          );
        }
      })
      .catch((err) => {
        showToast(err?.message || "Failed to send OTP", "error");
      });
  };

  const handleOtpSubmit = (values) => {
    const finalOTP = values.otp.join("");
    const formData = new FormData();
    formData.append("type", mode);
    formData.append("otp", finalOTP);
    if (mode == "email") {
      formData.append("email", values?.email);
    } else {
      formData.append("phone", values?.mobile);
    }

    dispatch(reKyc_UpdatePersonalDetailsThunk({ body: formData, token }))
      .unwrap()
      .then((res) => {
        console.log("res", res);
        if (res?.code === 200) {
          showToast(res?.message, "success");
          close_editMailMobile();
          clearError(mode);
        } else {
          showToast(res?.message || "Something went wrong, please try again", "error");
        }
      })
      .catch((err) => {
        showToast(err || "Something went wrong, please try again", "error");
      });
  };

  useEffect(() => {
    let interval;
    if (showOTP && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setResendActive(true);
    }
    return () => clearInterval(interval);
  }, [showOTP, timer]);

  return (
    <Modal
      isOpen={edit_MailMobile}
      centered={true}
      className="contact_modal_custom"
    >
      <ModalHeader
        toggle={close_editMailMobile}
        style={{
          backgroundColor: "#0F528D",
          paddingTop: "0.75rem",
          paddingBottom: "0.75rem",
        }}
      >
        <span className="fs-16 contact_note_header ">{heading}</span>
      </ModalHeader>
      <ModalBody className="">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(mode, showOTP)}
          onSubmit={(values) => {
            if (!showOTP) {
              handleSubmit(values);
            } else {
              handleOtpSubmit(values);
            }
          }}
        >
          {(formik) => (
            <Form>
              <ModalBody className="p-0">
                <Row className="m-0 p-0">
                  {mode === "phone" && !showOTP && (
                    <Col xxl={12} className="ps-0">
                      <FormGroup>
                        <Label for="mobile">Mobile Number *</Label>
                        <Input
                          type="text"
                          name="mobile"
                          id="mobile"
                          maxLength={10}
                          className="form-control"
                          placeholder="Enter new mobile number"
                          value={formik.values.mobile}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.mobile && !!formik.errors.mobile
                          }
                        />
                        <FormFeedback>{formik.errors.mobile}</FormFeedback>
                      </FormGroup>
                    </Col>
                  )}

                  {mode === "email" && !showOTP && (
                    <Col xxl={12} className="ps-0">
                      <FormGroup>
                        <Label for="email">Email *</Label>
                        <Input
                          type="email"
                          name="email"
                          id="email"
                          className="form-control"
                          placeholder="Enter new email"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.email && !!formik.errors.email
                          }
                        />
                        <FormFeedback>{formik.errors.email}</FormFeedback>
                      </FormGroup>
                    </Col>
                  )}

                  {showOTP && (
                    <>
                      <Col xxl={12} className="ps-0">
                        <FormGroup className="otp_resend_form">
                          <Label>
                            Enter OTP sent on{" "}
                            {mode === "email"
                              ? maskEmail(formik.values.email)
                              : maskMobile(formik.values.mobile)}{" "}
                            *
                          </Label>
                          <div className="d-flex gap-2">
                            {formik.values.otp.map((val, index) => (
                              <Input
                                key={index}
                                type="text"
                                maxLength={1}
                                className="form-control text-center"
                                name={`otp[${index}]`}
                                value={formik.values.otp[index]}
                                onChange={(e) => {
                                  const newOtp = [...formik.values.otp];
                                  newOtp[index] = e.target.value.replace(
                                    /\D/,
                                    ""
                                  );
                                  formik.setFieldValue("otp", newOtp);
                                  if (e.target.value && index < 3) {
                                    document
                                      .getElementById(`otp-${index + 1}`)
                                      ?.focus();
                                  }
                                }}
                                id={`otp-${index}`}
                              />
                            ))}
                          </div>
                          {formik.errors.otp &&
                            typeof formik.errors.otp === "string" && (
                              <FormFeedback className="d-block">
                                {formik.errors.otp}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <div className=" ps-0">
                        {resendActive ? (
                          <span
                            style={{
                              color: "#007bff",
                              cursor: "pointer",
                              fontWeight: 500,
                            }}
                            onClick={() => handleResend(formik.values)}
                          >
                            Resend OTP
                          </span>
                        ) : (
                          <span className="text-muted">
                            Resend OTP in{" "}
                            <span className="text-primary fw-medium">
                              {timer}s
                            </span>
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </Row>
              </ModalBody>

              <div className="centerContent gap-3">
                {showOTP && (
                  <Button
                    type="button"
                    className="btn btn-soft-secondary fs-16"
                    // style={{ minWidth: "120px" }}
                    onClick={() => setShowOTP(false)}
                  >
                    Back
                  </Button>
                )}
                <Button
                  color="primary"
                  type="submit"
                  className="fs-16 "
                  disabled={
                    reKyc_send_email_otpLoading ||
                    reKyc_UpdatePersonalDetailLoading ||
                    reKyc_send_phone_otpLoading
                  }
                  style={{ minWidth: "120px" }}
                >
                  {reKyc_send_email_otpLoading ||
                  reKyc_UpdatePersonalDetailLoading ||
                  reKyc_send_phone_otpLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : showOTP ? (
                    "Save"
                  ) : (
                    "Verify"
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default EditEmailMobileModal;
